import { create } from 'zustand';
import { isTenantSessionCurrent, readStoredTenantJwt, requireCurrentTenantSession } from '../utils/tenant-session';

export interface TenantInfo {
  id: string;
  tenant_type: 'personal' | 'enterprise' | 'group' | 'government';
  name: string;
  status: string;
  parent_tenant_id?: string | null;
  is_group_root?: boolean;
  // —— Phase 7-G: 企业信息扩展
  credit_code?: string | null;       // 统一社会信用代码（18 位）
  industry?: string | null;          // 行业（枚举见 ProfilePage）
  scale?: string | null;             // 规模（枚举见 ProfilePage）
  contact_name?: string | null;      // 联系人姓名
  contact_phone?: string | null;     // 联系电话（已有保留）
  logo_url?: string | null;          // 企业 logo 公网 URL
  business_scope?: string | null;
  address?: string | null;
  website?: string | null;
  // —— 在线客服：M 端首页「联系我们」右上角入口 → 客服页。
  // 与上面 contact_name/contact_phone（工商联系人）解耦；二维码非空才在 M 端显示入口。
  customer_service_name?: string | null;
  customer_service_phone?: string | null;
  customer_service_avatar_url?: string | null;
  customer_service_qr_url?: string | null;
}

export interface TenantUser {
  id: string;
  username?: string;
  nickname?: string;
  role: string;
}

interface State {
  jwt: string | null;
  user: TenantUser | null;
  tenant: TenantInfo | null;
  has_seat: boolean;
  must_change_password: boolean;
  setAuth: (jwt: string, user: TenantUser, tenant: TenantInfo, has_seat?: boolean, must_change_password?: boolean) => void;
  setUser: (user: TenantUser) => void;
  setTenant: (tenant: TenantInfo) => void;
  clearMustChangePassword: () => void;
  commitIfCurrent: (expectedJwt: string | null, update: () => void) => boolean;
  logoutIfCurrent: (expectedJwt: string | null) => boolean;
  logout: () => boolean;
}

const KEY_JWT = 'tenant_jwt';
const KEY_MCP = 'tenant_must_change_password';

export const useTenantAuth = create<State>((set, get) => ({
  jwt: localStorage.getItem(KEY_JWT),
  user: null,
  tenant: null,
  has_seat: false,
  must_change_password: localStorage.getItem(KEY_MCP) === '1',
  setAuth: (jwt, user, tenant, has_seat = false, must_change_password = false) => {
    const previousJwt = localStorage.getItem(KEY_JWT);
    const previousMcp = localStorage.getItem(KEY_MCP);
    try {
      if (must_change_password) localStorage.setItem(KEY_MCP, '1');
      else localStorage.removeItem(KEY_MCP);
      localStorage.setItem(KEY_JWT, jwt);
    } catch (error) {
      if (localStorage.getItem(KEY_JWT) === previousJwt) {
        if (previousMcp === null) localStorage.removeItem(KEY_MCP);
        else localStorage.setItem(KEY_MCP, previousMcp);
      }
      throw error;
    }
    set({ jwt, user, tenant, has_seat, must_change_password });
  },
  // 刷新后用 /me 水合 user（store 只持久化 jwt，user 在内存里，刷新会丢）
  setUser: (user) => { requireCurrentTenantSession(get().jwt, get().jwt); set({ user }); },
  setTenant: (tenant) => { requireCurrentTenantSession(get().jwt, get().jwt); set({ tenant }); },
  clearMustChangePassword: () => {
    requireCurrentTenantSession(get().jwt, get().jwt);
    localStorage.removeItem(KEY_MCP);
    set({ must_change_password: false });
  },
  commitIfCurrent: (expectedJwt, update) => {
    if (!isTenantSessionCurrent(expectedJwt, get().jwt, readStoredTenantJwt())) {
      requireCurrentTenantSession(expectedJwt, get().jwt);
      return false;
    }
    update();
    return true;
  },
  logoutIfCurrent: (expectedJwt) => {
    if (!isTenantSessionCurrent(expectedJwt, get().jwt, readStoredTenantJwt())) {
      requireCurrentTenantSession(expectedJwt, get().jwt);
      return false;
    }
    return get().logout();
  },
  logout: () => {
    requireCurrentTenantSession(get().jwt, get().jwt);
    localStorage.removeItem(KEY_JWT);
    localStorage.removeItem(KEY_MCP);
    set({ jwt: null, user: null, tenant: null, has_seat: false, must_change_password: false });
    return true;
  },
}));
